import { useState, type FormEvent } from 'react';
import Reveal from '@/components/feature/Reveal';
import { site } from '@/data/site';

const FORM_SUBMIT = "https://readdy.ai/api/form/darrgleos9pb7ea8f4ng";

type Status = "idle" | "loading" | "success" | "error";

const interestOptions = ["Ortsbesichtigung", "Angebot", "Beratung", "Sonstiges"];

export default function Contact() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    // Anti-spam honeypot
    const honeypot = String(formData.get("contact_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      return;
    }
    formData.delete("contact_alt");

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, String(value));
    });

    setStatus("loading");
    setFormError("");

    try {
      const response = await fetch(FORM_SUBMIT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          serverMsg || `Senden fehlgeschlagen (Status ${response.status}). Bitte erneut versuchen.`,
        );
      }
    } catch {
      setStatus("error");
      setFormError("Verbindung fehlgeschlagen. Bitte später erneut versuchen oder anrufen.");
    }
  };

  const inputClass =
    "w-full rounded-md border border-background-300 bg-background-50 px-4 py-3.5 font-body text-sm text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200";

  return (
    <section id="kontakt" className="relative bg-background-100 py-20 md:py-28 scroll-mt-24">
      <div className="w-full px-4 md:px-8">
        <Reveal className="max-w-2xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 font-label text-xs font-semibold uppercase tracking-[0.32em] text-primary-600">
            <span className="w-6 h-px bg-accent-500" />
            Kontakt
            <span className="w-6 h-px bg-accent-500" />
          </span>
          <h2 className="mt-5 font-heading text-3xl md:text-5xl font-semibold leading-tight text-foreground-950">
            Kostenlos &amp; unverbindlich anfragen
          </h2>
          <p className="mt-5 font-body text-base md:text-lg leading-relaxed text-foreground-600">
            Schildern Sie uns kurz Ihr Anliegen – wir melden uns zeitnah mit einer Einschätzung und
            einem Terminvorschlag.
          </p>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8 items-stretch">
          {/* Info panel */}
          <Reveal variant="left" className="lg:col-span-2">
            <div className="h-full rounded-2xl bg-primary-950 p-7 md:p-8 flex flex-col border border-primary-900">
              <h3 className="font-heading text-xl md:text-2xl font-semibold text-background-50">
                Direkt in Kontakt
              </h3>
              <p className="mt-3 font-body text-sm leading-relaxed text-background-100/75">
                Am schnellsten erreichen Sie uns telefonisch – oder schreiben Sie uns über das
                Formular.
              </p>

              <a
                href={site.phoneHref}
                className="mt-6 inline-flex items-center gap-3 rounded-md bg-accent-500 hover:bg-accent-400 text-foreground-950 font-label font-bold text-lg px-5 py-4 transition-all duration-300 hover:-translate-y-0.5 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-fill text-xl" />
                {site.phoneDisplay}
              </a>

              <ul className="mt-7 space-y-5">
                <li className="flex items-start gap-3">
                  <span className="w-9 h-9 shrink-0 rounded-md bg-background-50/[0.08] flex items-center justify-center">
                    <i className="ri-map-pin-2-line text-accent-400" />
                  </span>
                  <span className="font-body text-sm leading-relaxed text-background-100">
                    {site.street}
                    <br />
                    {site.city}
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-9 h-9 shrink-0 rounded-md bg-background-50/[0.08] flex items-center justify-center">
                    <i className="ri-mail-line text-accent-400" />
                  </span>
                  <a
                    href={site.emailHref}
                    className="font-body text-sm text-background-100 hover:text-accent-400 transition-colors cursor-pointer break-all"
                  >
                    {site.email}
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-9 h-9 shrink-0 rounded-md bg-background-50/[0.08] flex items-center justify-center">
                    <i className="ri-time-line text-accent-400" />
                  </span>
                  <span className="font-body text-sm text-background-100">
                    {site.hours.map((h) => (
                      <span key={h.day} className="block">
                        {h.day}: <span className="text-background-100/65">{h.time}</span>
                      </span>
                    ))}
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-9 h-9 shrink-0 rounded-md bg-background-50/[0.08] flex items-center justify-center">
                    <i className="ri-instagram-line text-accent-400" />
                  </span>
                  <a
                    href={site.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-body text-sm text-background-100 hover:text-accent-400 transition-colors cursor-pointer"
                  >
                    Instagram ansehen
                  </a>
                </li>
              </ul>

              <p className="mt-auto pt-8 border-t border-background-50/12 font-body text-xs leading-relaxed text-background-100/55">
                {site.name} · Inhaber {site.owner}
              </p>
            </div>
          </Reveal>

          {/* Form – highlighted card */}
          <Reveal variant="right" className="lg:col-span-3">
            <div className="relative h-full rounded-2xl p-[2px] bg-gradient-to-br from-accent-400 via-accent-500/40 to-accent-600/60 glow-accent-hover glow-accent">
              <form
                id="kontaktformular"
                data-readdy-form
                onSubmit={handleSubmit}
                className="h-full rounded-[14px] bg-background-50 p-6 md:p-9"
                noValidate={false}
              >
                <div className="flex items-center gap-2 text-accent-600">
                  <i className="ri-flashlight-fill" />
                  <span className="font-label text-xs font-bold uppercase tracking-[0.24em]">
                    Schnelle Anfrage
                  </span>
                </div>

                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block font-label text-sm font-semibold text-foreground-800 mb-2">
                      Name*
                    </label>
                    <input id="name" name="name" type="text" required className={inputClass} placeholder="Ihr Name" />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block font-label text-sm font-semibold text-foreground-800 mb-2">
                      Telefon*
                    </label>
                    <input id="phone" name="phone" type="tel" required className={inputClass} placeholder="Ihre Telefonnummer" />
                  </div>
                </div>

                <div className="mt-4">
                  <label htmlFor="email" className="block font-label text-sm font-semibold text-foreground-800 mb-2">
                    E-Mail*
                  </label>
                  <input id="email" name="email" type="email" required className={inputClass} placeholder="ihre@email.de" />
                </div>

                <div className="mt-4">
                  <label htmlFor="anliegen" className="block font-label text-sm font-semibold text-foreground-800 mb-2">
                    Anliegen
                  </label>
                  <select id="anliegen" name="anliegen" className={inputClass} defaultValue={interestOptions[0]}>
                    {interestOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mt-4">
                  <label htmlFor="message" className="block font-label text-sm font-semibold text-foreground-800 mb-2">
                    Nachricht
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    maxLength={500}
                    className={`${inputClass} resize-none`}
                    placeholder="Beschreiben Sie kurz Ihr Anliegen (z. B. Hecke schneiden, Rasen anlegen) und den Ort."
                  />
                  <p className="mt-1.5 font-body text-xs text-foreground-500">Maximal 500 Zeichen.</p>
                </div>

                {/* Anti-spam honeypot */}
                <input
                  type="text"
                  name="contact_alt"
                  className="contact-extra-note"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-4">
                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-background-50 font-label font-bold text-base px-8 py-4 transition-all duration-300 hover:-translate-y-0.5 whitespace-nowrap cursor-pointer w-full sm:w-auto"
                  >
                    <i className={status === "loading" ? "ri-loader-4-line animate-spin" : "ri-mail-send-line"} />
                    {status === "loading" ? "Wird gesendet…" : "Anfrage senden"}
                  </button>
                  <span className="inline-flex items-center gap-2 font-label text-sm text-foreground-600">
                    <i className="ri-shield-check-line text-primary-500 text-lg" />
                    Kostenlos &amp; unverbindlich
                  </span>
                </div>

                {status === "success" && (
                  <div className="mt-5 flex items-start gap-2.5 rounded-md border border-primary-200 bg-primary-50 px-4 py-3">
                    <i className="ri-checkbox-circle-fill text-primary-600 text-lg mt-0.5" />
                    <p className="font-body text-sm text-foreground-800">
                      Vielen Dank! Ihre Anfrage wurde übermittelt. Wir melden uns zeitnah bei Ihnen.
                    </p>
                  </div>
                )}
                {status === "error" && (
                  <div className="mt-5 flex items-start gap-2.5 rounded-md border border-accent-300 bg-accent-50 px-4 py-3">
                    <i className="ri-error-warning-fill text-accent-700 text-lg mt-0.5" />
                    <p className="font-body text-sm text-foreground-800">{formError}</p>
                  </div>
                )}
              </form>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}